import Vue from 'vue';
import { Connector } from '@/backendConnector';

export const CUSTOM_ROOM = 'custom';

export function getFurnitureCountInRoom(room) {
  let result = 0;
  if (room && room.meubles) {
    for (const furniture of room.meubles) {
      const qteType = parseInt(furniture.quantite_type, 10);
      result += qteType > 1 ? 1 : parseInt(furniture.quantity, 10);
    }
  }
  return result;
}

function isValidRooms(state) {
  return state.rooms.length > 0;
}

function isValidInventory(state) {
  return state.rooms.find(r => r && r.meubles && r.meubles.length > 0) !== undefined;
}

function isValidBoxFirstStep(state) {
  if (state.boxNumberKnown === 0) {
    return state.totalCartonNumber > 0 && state.flatType !== '';
  }
  if (state.boxNumberKnown === -1) {
    return state.totalCartonNumber === 0;
  }
  return state.totalCartonNumber > 0;
}

function isValidBoxLastStep(state) {
  return state.cartons.mover.cartons_livre
    || state.cartons.mover.cartons_penderie
    || state.cartons.mover.cartons_std;
}

function createRoomFurniture(room, furniture, quantity) {
  const result = {};
  const quantityType = furniture.quantite_type || 1;
  Vue.set(result, 'piece', room.id);
  Vue.set(result, 'name', furniture.nom_type);
  const calcVolume = furniture.volume_type / quantityType;
  Vue.set(result, 'volume', Math.round(calcVolume * (quantity || 1) * 10) / 10);
  Vue.set(result, 'volume_type', furniture.volume_type);
  Vue.set(result, 'quantity', quantity || 1);
  Vue.set(result, 'quantite_type', quantityType);
  Vue.set(result, 'demontage', false);
  Vue.set(result, 'demontage_type', furniture.demontage_type);
  Vue.set(result, 'is_demontable', furniture.is_demontable);
  Vue.set(result, 'emballage', furniture.emballage_type || false);
  Vue.set(result, 'heavy', furniture.heavy_type || false);
  Vue.set(result, 'type', furniture.id || furniture.type);
  Vue.set(result, 'icon', furniture.icon);
  Vue.set(result, 'observations', furniture.observations);
  if (!furniture.type || !furniture.id) {
    Vue.set(result, 'width', furniture.width);
    Vue.set(result, 'height', furniture.height);
    Vue.set(result, 'length', furniture.length);
  }
  return result;
}

function isDeletableRoom(room) {
  return room.meubles === undefined || room.meubles.length === 0;
}

function isMatchRegexNameRoomNb(nameRoom) {
  const regSpaceNumber = / [0-9]{1,}$/;
  const matchRegRoomName = nameRoom.match(regSpaceNumber);
  return matchRegRoomName;
}

function getRoomValue(room) {
  if (room.piece_type === CUSTOM_ROOM
    || room.value === CUSTOM_ROOM) return room.name.toLowerCase();
  const value = room.piece_type || room.value;
  return value.toLowerCase();
}

function isTotalMoverBoxesPerType(state, cartonType) {
  return state.totalCarton[cartonType] === state.cartons.mover[cartonType];
}

function isTotalMoverBoxes(state) {
  return isTotalMoverBoxesPerType(state, 'cartons_std')
    && isTotalMoverBoxesPerType(state, 'cartons_livre')
    && isTotalMoverBoxesPerType(state, 'cartons_penderie');
}

function adjustBoxesAccordingToTotalPerType(state, cartonType) {
  state.cartons.mover[cartonType] = Math.min(state.totalCarton[cartonType],
    state.cartons.mover[cartonType]);
  state.cartons.customer[cartonType] = state.totalCarton[cartonType]
    - state.cartons.mover[cartonType];
}

function adjustBoxesAccordingToTotal(state) {
  adjustBoxesAccordingToTotalPerType(state, 'cartons_std');
  adjustBoxesAccordingToTotalPerType(state, 'cartons_livre');
  adjustBoxesAccordingToTotalPerType(state, 'cartons_penderie');
}

const state = {
  preDefinedRooms: [],
  rooms: [],
  furnituresList: [],
  boxsList: [],
  services: [],
  servicesLoading: false,
  totalCartonNumber: 0,
  totalCarton: {
    cartons_std: 0.0,
    cartons_penderie: 0.0,
    cartons_livre: 0.0,
  },
  cartons: {
    customer: {
      cartons_std: 0.0,
      cartons_penderie: 0.0,
      cartons_livre: 0.0,
    },
    mover: {
      cartons_std: 0.0,
      cartons_penderie: 0.0,
      cartons_livre: 0.0,
    },
  },
  flatType: '',
  boxNumberKnown: null,
  displayCannotSuppressCard: false,
  furnituresListLoading: false,
  roomsLoading: false,
};

const actions = {
  getPreDefinedRooms({ commit }) {
    return Connector.getInstance()
      .getRoomType()
      .then((rooms) => {
        commit('SET_PRE_DEFINED_ROOMS', rooms);
      });
  },
  getRooms({ commit }) {
    commit('SET_ROOMS_LOADING', true);
    return Connector.getInstance().getRooms()
      .then((rooms) => {
        commit('SET_ROOMS', rooms);
      }).catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        commit('SET_ROOMS_LOADING', false);
      });
  },
  getFurnituresList({ commit }) {
    commit('SET_FURNITURES_LIST_LOADING', true);
    return Connector.getInstance().getFurnitureTypes()
      .then((furniture) => {
        if (furniture) {
          furniture = furniture.map((standard) => {
            standard.meubles_type = standard.meubles_type.map(
              (type) => {
                Vue.set(type, 'quantity', 0);
                Vue.set(type, 'demontage', false);
                return type;
              },
            );
            return standard;
          });
        }
        commit('SET_FURNITURES_LIST', furniture);
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        commit('SET_FURNITURES_LIST_LOADING', false);
      });
  },
  postRooms({ commit }, rooms) {
    commit('SET_ROOMS', rooms);
  },
  getServices({ commit }) {
    commit('SET_SERVICES_LOADING');
    return Connector.getInstance().getSpecialService()
      .then((data) => {
        commit('SET_SERVICES', data);
      }).catch((error) => {
        throw new Error(error);
      })
      .finally(() => {
        commit('UNSET_SERVICES_LOADING');
      });
  },
  deleteRoom({ commit }, roomId) {
    commit('SET_FURNITURES_LIST_LOADING', true);
    return Connector.getInstance().deleteRoom(roomId)
      .then(() => {
        commit('DELETE_ROOM', roomId);
      })
      .catch((e) => {
        /* eslint-disable no-alert, no-console */
        console.error(e);
      })
      .finally(() => {
        commit('SET_FURNITURES_LIST_LOADING', false);
      });
  },
  setRooms({ commit }, rooms) {
    commit('SET_ROOMS', rooms);
  },
  roomsSaveState({
    commit,
    rootGetters,
    getters,
  }) {
    commit('SET_ROOMS_LOADING', true);
    return Connector.getInstance()
      .saveRooms(getters.rooms, rootGetters['projectStore/projectId'])
      .then((rooms) => {
        commit('SET_ROOMS_FROM_SERVER', rooms);
        commit('SET_ROOMS_LOADING', false);
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        commit('SET_ROOMS_LOADING', false);
      });
  },
  inventorySaveState({
    getters,
  }) {
    return Connector.getInstance().updateRooms(getters.rooms);
  },
  // eslint-disable-next-line no-empty-pattern
  updateRoom({}, room) {
    return Connector.getInstance().updateRoom(room.id, room);
  },
  boxDeliverySaveState({ getters }) {
    return Connector.getInstance().postBoxes(getters.cartons);
  },
};

const getters = {
  boxNumberKnown: state => state.boxNumberKnown,
  preDefinedRooms: state => state.preDefinedRooms,
  rooms: state => state.rooms,
  roomIndex: state => index => state.rooms[index],
  furnituresList: state => state.furnituresList,
  services: state => state.services,
  flatType: state => state.flatType,
  totalBoxes: state => state.totalCartonNumber,
  totalCarton: state => state.totalCarton,
  cartons: state => state.cartons,
  roomsLoading: state => state.roomsLoading,
  furnituresListLoading: state => state.furnituresListLoading,
  displayCannotSuppressCard: state => state.displayCannotSuppressCard,
  'rooms/isValidForm': isValidRooms,
  'inventory/isValidForm': isValidInventory,
  'boxConfig/isValidForm': isValidBoxFirstStep,
  'boxDelivery/isValidForm': isValidBoxLastStep,
  hasEmptyRooms: (state) => {
    for (const room of state.rooms) {
      if (getFurnitureCountInRoom(room) === 0) {
        return true;
      }
    }
    return false;
  },
};

const mutations = {
  SET_FLAT_TYPE(state, type) {
    state.flatType = type;
  },
  SET_PRE_DEFINED_ROOMS(state, rooms) {
    state.preDefinedRooms = rooms;
  },
  SET_ROOMS(state, rooms) {
    state.rooms = rooms.filter(r => r !== undefined)
      .map((r) => {
        r.meubles = r.meubles
          .map((m) => {
            m.quantity = parseInt(m.quantity, 10);
            return m;
          });
        return r;
      });
  },
  SET_ROOMS_FROM_SERVER(state, rooms) {
    const roomsSavedRoom = state.rooms.filter(r => r.id !== undefined);
    const roomIds = roomsSavedRoom.map(r => r.id);
    const newRooms = rooms.filter(r => !roomIds.includes(r.id));
    newRooms.push(...roomsSavedRoom);
    state.rooms = newRooms;
  },
  SET_CANNOT_DISPLAY_CANNOT_SUPPRESS_CARD(state, value) {
    state.displayCannotSuppressCard = value;
  },
  SET_ROOM(state, room) {
    const roomValue = getRoomValue(room);
    const rooms = state.rooms.filter((stateRoom) => {
      const sRoomValue = getRoomValue(stateRoom);
      return sRoomValue === roomValue;
    });

    if (room.nb < rooms.length) {
      const savedRooms = rooms.filter(r => r.id !== undefined);
      const stateRoomNb = rooms.length - savedRooms.length;
      let deletedIndex;
      if (stateRoomNb > 0) {
        deletedIndex = state.rooms
          .findIndex(r => getRoomValue(r) === roomValue && r.id === undefined);
      } else {
        deletedIndex = state.rooms
          .findIndex(r => getRoomValue(r) === roomValue && isDeletableRoom(r));
        if (deletedIndex === -1) {
          state.displayCannotSuppressCard = true;
          room.nb += 1;
          return;
        }
      }
      if (state.rooms[deletedIndex].id !== undefined) {
        this.dispatch('furnituresStore/deleteRoom', state.rooms[deletedIndex].id);
      }
      const roomPieceType = state.rooms[deletedIndex].piece_type;
      state.rooms.splice(deletedIndex, 1);
      this.commit('furnituresStore/SET_ROOMS_NAME_PIECE_TYPE', roomPieceType);
      return;
    }

    const newRoom = {
      name: `${room.name}${room.nb > 1 ? ` ${room.nb}` : ''}`,
      piece_type: room.value || room.piece_type,
      meubles: [],
    };

    Vue.set(state.rooms, state.rooms.length, newRoom);
    this.commit('furnituresStore/SET_ROOMS_NAME_PIECE_TYPE', newRoom.piece_type);
  },
  SET_ROOM_NAME(state, { id, name }) {
    const room = state.rooms.find(r => r.id === id);
    room.name = name;
  },
  SET_ROOM_FURNITURE_DISASSEMBLY(state, { room, furniture, disassembly }) {
    const stateRoom = state.rooms.find(r => r.id === room.id);
    const roomFurniture = stateRoom.meubles.find(f => f.name === furniture.nom_type
      || f.name === furniture.name);

    if (roomFurniture) roomFurniture.demontage = disassembly;
    else {
      const f = createRoomFurniture(stateRoom, furniture);
      f.demontage = disassembly;
      Vue.set(stateRoom.meubles, stateRoom.meubles.length, f);
    }
  },
  SET_ROOM_FURNITURE_QUANTITY(state, { room, furniture, quantity }) {
    const stateRoom = state.rooms.find(r => r.id === room.id);
    if (stateRoom) {
      const index = stateRoom.meubles
        .findIndex(f => f.name === furniture.nom_type
          || f.name === furniture.name);
      if (index !== -1) {
        stateRoom.meubles[index].quantity = quantity;
        const calcVolume = furniture.volume_type / furniture.quantite_type;
        stateRoom.meubles[index].volume = Math.round(calcVolume * quantity * 10) / 10;
      } else {
        const f = createRoomFurniture(stateRoom, furniture, quantity);
        Vue.set(stateRoom.meubles, stateRoom.meubles.length, f);
      }
    }
  },
  DELETE_ROOM(state, roomId) {
    const roomIndex = state.rooms.findIndex(({ id }) => id === roomId);
    if (roomIndex !== -1) {
      const roomPieceType = state.rooms[roomIndex].piece_type;
      state.rooms.splice(roomIndex, 1);
      this.commit('furnituresStore/SET_ROOMS_NAME_PIECE_TYPE', roomPieceType);
    }
  },
  SET_ROOMS_NAME_PIECE_TYPE(state, pieceType) {
    const roomsIndex = [];
    state.rooms.forEach((room, index) => {
      if (room.piece_type === pieceType) {
        roomsIndex.push(index);
      }
    });

    if (roomsIndex.length) {
      roomsIndex.forEach((roomI, index) => {
        const matchRegRoomNameNb = isMatchRegexNameRoomNb(state.rooms[roomI].name);
        const oldName = state.rooms[roomI].name;
        if (matchRegRoomNameNb) {
          state.rooms[roomI].name = state.rooms[roomI].name.replace(matchRegRoomNameNb, roomsIndex.length > 1 ? ` ${index + 1}` : '');
        } else {
          state.rooms[roomI].name = roomsIndex.length > 1 ? `${state.rooms[roomI].name} ${index + 1}` : state.rooms[roomI].name;
        }
        if (state.rooms[roomI].id && state.rooms[roomI].name !== oldName) {
          this.dispatch('furnituresStore/updateRoom', state.rooms[roomI]);
        }
      });
    }
  },
  SET_ROOMS_LOADING(state, value) {
    state.roomsLoading = value;
  },
  SET_FURNITURES_LIST(state, furnituresList) {
    state.furnituresList = furnituresList;
  },
  SET_FURNITURES_LIST_LOADING(state, value) {
    state.furnituresListLoading = value;
  },
  SET_SERVICES_LOADING(state) {
    state.servicesLoading = true;
  },
  UNSET_SERVICES_LOADING(state) {
    state.servicesLoading = false;
  },
  SET_BOX_NUMBER_KNOWN(state, value) {
    state.boxNumberKnown = value;
  },
  SET_TOTAL_CARTON(state, total) {
    const isAllMoverBoxes = isTotalMoverBoxes(state);

    state.totalCarton = Object.assign({}, total);
    let t = 0;
    t += total.cartons_livre;
    t += total.cartons_penderie;
    t += total.cartons_std;
    state.totalCartonNumber = t;

    if (isAllMoverBoxes) {
      this.commit('furnituresStore/SET_MOVER_CARTON');
    } else {
      adjustBoxesAccordingToTotal(state);
    }
  },
  SET_USER_CARTON(state, { key, value }) {
    state.cartons.customer[key] = state.totalCarton[key] - value;
    state.cartons.mover[key] = value;
  },
  SET_MOVER_CARTON(state) {
    state.cartons.mover.cartons_penderie = state.totalCarton.cartons_penderie;
    state.cartons.mover.cartons_std = state.totalCarton.cartons_std;
    state.cartons.mover.cartons_livre = state.totalCarton.cartons_livre;

    state.cartons.customer.cartons_penderie = 0;
    state.cartons.customer.cartons_std = 0;
    state.cartons.customer.cartons_livre = 0;
  },
  SET_CARTONS(state, cartons) {
    state.cartons.customer = cartons.customer;
    state.cartons.mover = cartons.mover;
    state.totalCarton.cartons_livre = cartons.mover.cartons_livre + cartons.customer.cartons_livre;
    state.totalCarton.cartons_std = cartons.mover.cartons_std + cartons.customer.cartons_std;
    // eslint-disable-next-line max-len
    state.totalCarton.cartons_penderie = cartons.mover.cartons_penderie + cartons.customer.cartons_penderie;
    let total = 0;
    total += cartons.customer.cartons_livre;
    total += cartons.customer.cartons_penderie;
    total += cartons.customer.cartons_std;
    total += cartons.mover.cartons_livre;
    total += cartons.mover.cartons_penderie;
    total += cartons.mover.cartons_std;
    state.totalCartonNumber = total;
    if (state.cartons.cartons_init && total === 0) {
      state.boxNumberKnown = -1;
    }
  },
};


export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
